import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import axios from 'axios';

import {
  Popup,
  Heading,
  Button,
  Status,
  RangeSlider,
  Preloader,
  I18n,
} from 'components';

import { formatPrice } from 'utils/formatString';
import styles from './styles.pcss';

const cx = classNames.bind(styles);

export class SpeedUpPopup extends Component {
  constructor(props) {
    super(props);

    this.state = {
      options: null,
      selectedSpeedUp: null,
      requestId: null,
      isNotEnoughMoney: false,
      error: null,
      isLoading: false,
    };
  }

  componentDidMount() {
    const { baseSpeed, basePrice } = this.props;
    const speedUpsWithRequest = this.props.speedUps.filter((s) => s.isConnectByRequest);
    const speedUpsSimple = this.props.speedUps
      .filter((s) => !s.isConnectByRequest)
      .slice(0, 10 - speedUpsWithRequest.length);
    const speedUps = [...speedUpsSimple, ...speedUpsWithRequest];
    const connectedSpeedUp = speedUps.find((s) => s.isConnected);

    setTimeout(() => {
      this.setState({
        options: [
          {
            value: 0,
            speed: baseSpeed / 1000,
            price: basePrice,
            position: 0,
            isConnected: !connectedSpeedUp,
            serviceId: null,
            splId: null,
          },
          ...speedUps.map((speedUp, index) => ({
            ...speedUp,
            value: speedUp.speedUp,
            speed: (speedUp.speedUp + baseSpeed) / 1000,
            price: speedUp.price + basePrice,
            position: (index + 1) / speedUps.length * 100,
          })),
        ],
      });
    });
  }

  getFee = () => {
    const { options, selectedSpeedUp } = this.state;

    const connectedIndex = options.findIndex((s) => s.isConnected);
    const selectedIndex = options.findIndex((s) => s.serviceId === selectedSpeedUp.serviceId);

    if (selectedIndex === 0) return 0;
    if (connectedIndex === 0) return selectedSpeedUp.connectFee;
    if (selectedIndex > connectedIndex) return selectedSpeedUp.moveUpFee;
    if (selectedIndex < connectedIndex) return selectedSpeedUp.moveDownFee;

    return 0;
  };

  getNewTotalRcRate = () => {
    const { totalRcRate } = this.props;
    const { options, selectedSpeedUp } = this.state;

    const connectedSpeedUp = options.find((s) => s.isConnected);
    const priceDelta = selectedSpeedUp.price - connectedSpeedUp.price;

    return formatPrice(totalRcRate + priceDelta);
  };

  updateSelectedSpeedUp = (value) => {
    this.setState((prevState) => this.setState({
      selectedSpeedUp: prevState.options.find((s) => s.value === value),
    }));
  };

  saveSpeed = async () => {
    const { presetId } = this.props;
    const { selectedSpeedUp, options } = this.state;

    const connectedSpeedUp = options.find((s) => s.isConnected);

    this.setState({ isLoading: true, error: null });

    try {
      const { data } = await axios.post('/api/profile/homeinternet/changespeedup/', {
        presetId,
        serviceId: selectedSpeedUp.serviceId,
        splId: connectedSpeedUp.splId,
      });

      this.setState({
        requestId: data.requestId || null,
        isNotEnoughMoney: data.isNotEnoughMoney,
        addBalanceUrl: data.addBalanceUrl,
        isLoading: false,
      });
    } catch (error) {
      this.setState({
        error,
        isLoading: false,
      });
    }
  };

  checkConnection = async () => {
    const { selectedSpeedUp } = this.state;

    this.setState({ isLoading: true, error: null });

    try {
      const { data } = await axios.post('/api/profile/homeinternet/increasespeedbyrequest/', {
        newSpeed: selectedSpeedUp.speed,
      });

      this.setState({
        requestId: data.requestId,
      });
    } catch (error) {
      this.setState({
        error,
        isLoading: false,
      });
    }
  };

  render() {
    const {
      baseSpeed,
      balance,
      onClose,
    } = this.props;
    const {
      options,
      selectedSpeedUp,
      requestId,
      isNotEnoughMoney,
      addBalanceUrl,
      error,
      isLoading,
    } = this.state;

    if (!options) return null;

    const connectFee = selectedSpeedUp ? this.getFee() : 0;

    return (
      <Popup
        onClose={onClose}
        opened
      >
        {!requestId && !isNotEnoughMoney && !error && (
          <>
            <Heading className={cx('heading')} level={1}>
              <I18n path="internet.speedUp.heading" />
            </Heading>

            {!isLoading ? (
              <>
                <div className={cx('description')}>
                  <I18n path="internet.speedUp.description" />
                </div>
                <div className={cx('baseSpeed')}>
                  <I18n
                    path="internet.speedUp.baseSpeed"
                    vars={{ speed: baseSpeed / 1000 }}
                  />
                </div>
                <div className={cx('speedSelector')}>
                  <div className={cx('sliderScaleLabel')}>
                    <I18n path="internet.speedUp.newSpeedLabel" />
                  </div>
                  <div className={cx('sliderScale', 'speedScale')}>
                    {options.map((o) => (
                      <span
                        key={o.serviceId}
                        className={cx('sliderScaleItem')}
                      >
                        {o.speed}
                      </span>
                    ))}
                  </div>
                  <div className={cx('slider')}>
                    <RangeSlider
                      values={options}
                      initialPosition={options.find((o) => o.isConnected).position}
                      onValueChange={this.updateSelectedSpeedUp}
                      animate
                      sticky
                    />
                  </div>
                  <div className={cx('sliderScale', 'priceScale')}>
                    {options.map((o) => (
                      <span
                        key={o.serviceId}
                        className={cx('sliderScaleItem')}
                      >
                        {o.price}
                      </span>
                    ))}
                  </div>
                  <div className={cx('sliderScaleLabel')}>
                    <I18n path="internet.speedUp.rateLabel" />
                  </div>
                </div>

                {(!selectedSpeedUp || selectedSpeedUp.isConnected) ? (
                  <>
                    <div className={cx('message')}>
                      <I18n path="internet.speedUp.selectNewSpeed" />
                    </div>

                    <div className={cx('button')}>
                      <Button
                        className="big"
                        onClick={this.saveSpeed}
                        disabled
                        wide
                      >
                        <I18n path="common.save" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <I18n
                      className={cx('totalRow')}
                      path="internet.speedUp.connectPrice"
                      vars={{ price: connectFee ? `${connectFee} ₽` : 'бесплатно' }}
                    />

                    <div className={cx('button')}>
                      <Button
                        className="big"
                        onClick={this.saveSpeed}
                        disabled={!selectedSpeedUp || selectedSpeedUp.isConnected}
                        wide
                      >
                        <I18n path="common.save" />
                      </Button>
                    </div>

                    <I18n
                      className={cx('totalRow')}
                      path="internet.speedUp.totalPrice"
                      vars={{ price: this.getNewTotalRcRate() }}
                    />
                  </>
                )}

                {selectedSpeedUp && selectedSpeedUp.isConnectByRequest && (
                  <>
                    <Status
                      className={['attention', cx('status')]}
                      emoji="statusFail"
                    >
                      <I18n path="internet.speedUp.connectionByRequest" />
                    </Status>

                    <div className={cx('button')}>
                      <Button
                        className="big"
                        onClick={this.checkConnection}
                        wide
                      >
                        <I18n path="internet.speedUp.checkConnection" />
                      </Button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <Preloader
                className={cx('preloader')}
                size="80"
              />
            )}
          </>
        )}

        {requestId && (
          <div className={cx('message')}>
            <Heading className={cx('title')} level={1}>
              Запрос успешно
              <br />
              отправлен
            </Heading>
            <Heading className={cx('subtitle')} level={5}>
              Запрос на изменение скорости №
              {requestId}
              {' '}
              успешно отправлен
            </Heading>
            <div className={cx('button')}>
              <Button
                className="big"
                onClick={onClose}
                wide
              >
                Продолжить работу
              </Button>
            </div>
          </div>
        )}

        {isNotEnoughMoney && (
          <div className={cx('message')}>
            <Heading className={cx('title')} level={1}>
              Недостаточно средств
              {' '}
              на балансе
            </Heading>
            <Heading className={cx('subtitle')} level={5}>
              Для подключения необходимо оплатить
              {' '}
              {connectFee - balance}
              {' '}
              ₽
            </Heading>
            <div className={cx('button')}>
              <Button
                className="big"
                href={addBalanceUrl}
                onClick={onClose}
                target="_blank"
                wide
              >
                Пополнить баланс
              </Button>
            </div>
          </div>
        )}

        {error && (
          <div className={cx('message')}>
            <Heading className={cx('title')} level={1}>
              <I18n path="common.somethingWentWrong" />
            </Heading>
            <div className={cx('button')}>
              <Button
                className="big"
                onClick={onClose}
              >
                Продолжить работу
              </Button>
            </div>
          </div>
        )}
      </Popup>
    );
  }
}

SpeedUpPopup.propTypes = {
  speedUps: PropTypes.array,
  baseSpeed: PropTypes.number,
  basePrice: PropTypes.number,
  presetId: PropTypes.string,
  balance: PropTypes.number,
  totalRcRate: PropTypes.number,
  onClose: PropTypes.func,
};
